import BaseEllipsisText from '@/components/base/BaseEllipsisText';
import BaseFieldValue, {
  type BaseFieldValueProps,
} from '@/components/base/BaseFieldValue';
import { Tooltip, type TableColumnsType } from 'antd';
import type { ColumnType } from 'antd/es/table';
import type { ColumnTitleProps } from 'antd/es/table/interface';
import type { ReactNode } from 'react';

export interface BaseTableColumn<T extends object>
  extends ColumnType<T>,
    Omit<BaseFieldValueProps, 'value' | 'timeZone'> {
  children?: BaseTableColumn<T>[];
  titleTooltip?: string;
  /** 编号、时间或组合订单等不可拆分内容按实际内容定宽。 */
  contentWidth?: boolean;
}

/** 列头说明与省略提示共用，原生函数列头也在求值后处理。 */
function renderTitle(title: ReactNode, tooltip?: string) {
  if (tooltip) {
    return (
      <Tooltip title={tooltip}>
        <span className="base-table-column-tip block overflow-hidden text-ellipsis whitespace-nowrap cursor-help underline decoration-dotted underline-offset-[3px]">
          {title}
        </span>
      </Tooltip>
    );
  }
  return typeof title === 'string' ? <BaseEllipsisText title={title} /> : title;
}

/** 保留原生列能力和自定义 render，统一补齐标准展示与列头提示。 */
export function buildTableColumns<T extends object>(
  columns: BaseTableColumn<T>[],
): TableColumnsType<T> {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return columns.map((column) => {
    const {
      children,
      valueType,
      format,
      valueEnum,
      valueClassName,
      valueSuffix,
      valueStrong,
      fontSize,
      titleTooltip,
      contentWidth = valueType === 'code' ||
        valueType === 'date' ||
        valueType === 'dateTime',
      ...nativeColumn
    } = column;
    const originalTitle = column.title;
    const title =
      typeof originalTitle === 'function'
        ? (props: ColumnTitleProps<T>) =>
            renderTitle(originalTitle(props), titleTooltip)
        : renderTitle(originalTitle, titleTooltip);
    if (children)
      return { ...nativeColumn, title, children: buildTableColumns(children) };
    return {
      ...nativeColumn,
      ...(contentWidth && {
        minWidth:
          column.minWidth ??
          (typeof column.width === 'number' ? column.width : undefined),
        className: [column.className, 'base-table-cell-content']
          .filter(Boolean)
          .join(' '),
      }),
      title,
      render:
        column.render ??
        ((value: unknown) => (
          <BaseFieldValue
            value={value}
            valueType={valueType}
            format={format}
            valueEnum={valueEnum}
            valueClassName={valueClassName}
            valueSuffix={valueSuffix}
            valueStrong={valueStrong}
            fontSize={fontSize}
            timeZone={timeZone}
          />
        )),
    };
  });
}
